import { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ArrowRight, Sparkles } from 'lucide-react';
import useApi from '../api';
import useAnalytics from '../analytics/analytics';
import { useUser } from '../user/UserContext';
import PlanPicker from '../billing/PlanPicker';
import useCheckout from '../billing/useCheckout';
import type { BillingSummary, CreditPlan } from '../billing/types';

/**
 * Upgrade modal: one price card with the monthly-credit dropdown
 * (CREDIT_PLANS_PLAN.md). Reads the ladder from the API — no Stripe price ids
 * live in the frontend anymore.
 */
export default function Upgrade({
  showModal,
  setShowModal,
  sessionId,
  upgradeSuccess,
}: {
  showModal: any;
  setShowModal: any;
  sessionId: string | null;
  upgradeSuccess?: boolean;
}) {
  const { get } = useApi();
  const { captureEvent } = useAnalytics();
  const { organization } = useUser();
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [changed, setChanged] = useState(false);

  const billingParam = new URLSearchParams(window.location.search).get('billing');
  const showSuccess =
    !!sessionId || !!upgradeSuccess || billingParam === 'success' || billingParam === 'changed' || changed;

  const fetchSummary = useCallback(async () => {
    try {
      const response = await get('/billing/summary');
      setSummary(response?.data as BillingSummary);
    } catch (error) {
      console.error('Error loading billing summary:', error);
    }
  }, [get]);

  useEffect(() => {
    if (showModal) void fetchSummary();
  }, [showModal, fetchSummary]);

  const { checkout, loading } = useCheckout({
    onChanged: () => {
      setChanged(true);
      void fetchSummary();
    },
  });

  const handleSelect = async (planId: string) => {
    const response = await get('/billing/plans');
    const plan = ((response?.data?.plans || []) as CreditPlan[]).find((p) => p.id === planId);
    if (!plan) return;
    await checkout(plan);
  };

  const closeAndClearSession = () => {
    setShowModal(false);
    window.history.pushState({}, document.title, window.location.pathname);
    captureEvent('plan_window_closed', {});
  };

  const hasSubscription = !!(summary?.has_subscription ?? organization?.has_subscription);
  const currentPlanId = summary?.is_credits_plan ? summary.plan?.id ?? null : null;

  return (
    <Dialog open={showModal} onOpenChange={closeAndClearSession}>
      <DialogContent className="max-w-[560px] p-0 gap-0 overflow-hidden border-border/50">
        {!showSuccess ? (
          <div className="w-full">
            <div className="px-8 pt-8 pb-4">
              <DialogHeader>
                <DialogTitle className="text-2xl font-semibold tracking-tight text-center">
                  Choose your monthly credits
                </DialogTitle>
                <p className="text-center text-sm text-muted-foreground mt-1.5">
                  Every capability is included. Change your amount any time.
                </p>
              </DialogHeader>
            </div>
            <div className="px-6 pb-8 pt-2">
              <PlanPicker
                currentPlanId={currentPlanId}
                hasSubscription={hasSubscription}
                onSelect={handleSelect}
                loading={loading}
              />
            </div>
          </div>
        ) : (
          <div className="w-full p-12 flex items-center justify-center flex-col gap-4">
            <div className="p-3 rounded-full bg-primary/10 mb-2">
              <Sparkles className="h-8 w-8 text-primary" />
            </div>
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold tracking-tight text-center">
                {changed || upgradeSuccess || billingParam === 'changed'
                  ? 'Plan updated'
                  : 'Thank you — you’re all set'}
              </DialogTitle>
              <p className="text-center text-sm text-muted-foreground mt-2 max-w-md">
                {changed || upgradeSuccess || billingParam === 'changed'
                  ? 'Your monthly credits have been updated. Upgrades apply right away; downgrades take effect at your next renewal.'
                  : 'Your credits are ready and your agent keeps working. You can change your monthly amount any time from Billing.'}
              </p>
            </DialogHeader>
            <Button onClick={closeAndClearSession} className="mt-4">
              Continue to Dashboard
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
